import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const res = await fetch(`${API_URL}/api/testimonials/active`);
        if (res.ok) {
          const data = await res.json();
          setTestimonials(data);
        }
      } catch (err) {
        console.error('Error loading testimonials:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  if (loading || testimonials.length === 0) return null;

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section data-testid="testimonials-section" className="py-16 md:py-24 bg-gradient-to-b from-[#0a0a0a] to-[#141414]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Lo que dicen nuestros colaboradores
          </h2>
          <div className="w-24 h-1 bg-[#e4002b] mx-auto rounded-full"></div>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-[#e4002b]/50 transition-all"
                aria-label="Anterior testimonio"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-[#e4002b]/50 transition-all"
                aria-label="Siguiente testimonio"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </>
          )}

          {/* Testimonial Card */}
          <div className="bg-[#1a1a1a] rounded-2xl p-8 md:p-12 mx-8 md:mx-16 border border-white/5 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Person Image */}
              <div className="flex-shrink-0">
                {currentTestimonial.person_image ? (
                  <img
                    src={currentTestimonial.person_image}
                    alt={currentTestimonial.person_name}
                    className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-4 border-[#e4002b] shadow-lg shadow-[#e4002b]/20"
                  />
                ) : (
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-[#e4002b] to-[#ff4444] flex items-center justify-center shadow-lg shadow-[#e4002b]/20">
                    <span className="text-4xl md:text-5xl font-bold text-white">
                      {currentTestimonial.person_name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Quote and Info */}
              <div className="flex-1 text-center md:text-left">
                {/* Quote Icon */}
                <Quote className="w-10 h-10 text-[#e4002b]/30 mb-4 mx-auto md:mx-0 rotate-180" />
                
                {/* Quote Text */}
                <blockquote className="text-white/90 text-lg md:text-xl leading-relaxed mb-6 font-light">
                  "{currentTestimonial.quote}"
                </blockquote>

                {/* Person Info */}
                <div className="border-t border-white/10 pt-6">
                  <p className="text-white font-semibold text-lg">{currentTestimonial.person_name}</p>
                  <p className="text-[#e4002b] font-medium">{currentTestimonial.position}</p>
                  <p className="text-white/50 text-sm mt-1">{currentTestimonial.cinema_name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dots Indicator */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-[#e4002b] w-8'
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Ir al testimonio ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
